/**
 * Support desk mailbox — IMAP ingests all inbound mail into Support Desk (Settings → Support Desk Module)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Mail as MailIcon, Cloud as CloudIcon } from '@mui/icons-material';
import apiService from '../services/api';

interface SupportDeskMailboxesTabProps {
  onError?: (msg: string) => void;
}

const SupportDeskMailboxesTab: React.FC<SupportDeskMailboxesTabProps> = ({ onError }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    imap_server: 'imap.sonicmail.net.au',
    imap_port: 993,
    imap_use_ssl: true,
    username: 'support@example.com',
    password: '',
    is_enabled: false,
    poll_interval_seconds: 60,
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [oauthConnected, setOauthConnected] = useState(false);
  const [authMethod, setAuthMethod] = useState('password');
  const [lastPolledAt, setLastPolledAt] = useState<string | null>(null);
  const [oauthBusy, setOauthBusy] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const config = await apiService.getSupportDeskMailboxConfig();
      setForm((f) => ({
        ...f,
        imap_server: config.imap_server || 'imap.sonicmail.net.au',
        imap_port: config.imap_port ?? 993,
        imap_use_ssl: config.imap_use_ssl ?? true,
        username: config.username || '',
        is_enabled: config.is_enabled ?? false,
        poll_interval_seconds: config.poll_interval_seconds ?? 60,
        password: '',
      }));
      setPasswordSet(config.password_set ?? false);
      setOauthConnected(config.microsoft_oauth_connected ?? false);
      setAuthMethod(config.auth_method ?? 'password');
      setLastPolledAt(config.last_polled_at ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const st = q.get('support_desk_oauth');
    if (!st) return;
    const detail = q.get('support_desk_oauth_detail');
    q.delete('support_desk_oauth');
    q.delete('support_desk_oauth_detail');
    const path = `${window.location.pathname}${q.toString() ? `?${q.toString()}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', path);
    if (st === 'success') {
      setError(null);
      fetchConfig();
    } else if (st === 'error') {
      setError(detail || 'Microsoft sign-in failed');
    }
  }, []);

  // Refresh status periodically when polling is enabled
  useEffect(() => {
    if (!form.is_enabled) return;
    const t = setInterval(fetchConfig, 30000);
    return () => clearInterval(t);
  }, [form.is_enabled]);

  const handleConnectMicrosoft = async () => {
    if (!form.imap_server.trim() || !form.username.trim()) {
      setError('Set IMAP server and mailbox email first (e.g. support@sonicnet.net.au), then connect.');
      return;
    }
    setOauthBusy(true);
    setError(null);
    try {
      await apiService.updateSupportDeskMailboxConfig({
        imap_server: form.imap_server.trim(),
        imap_port: form.imap_port,
        imap_use_ssl: form.imap_use_ssl,
        username: form.username.trim(),
        poll_interval_seconds: Math.max(10, form.poll_interval_seconds),
        is_enabled: form.is_enabled,
      });
      const { authorization_url } = await apiService.getSupportDeskMailboxMicrosoftOAuthUrl();
      window.location.href = authorization_url;
    } catch (e: any) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Could not start Microsoft sign-in. Check server OAuth env vars.');
    } finally {
      setOauthBusy(false);
    }
  };

  const handleDisconnectMicrosoft = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskMailboxConfig({ clear_microsoft_oauth: true });
      await fetchConfig();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to disconnect Microsoft');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.imap_server.trim() || !form.username.trim()) {
      setError('Server and mailbox email (username) are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSupportDeskMailboxConfig({
        imap_server: form.imap_server.trim(),
        imap_port: form.imap_port,
        imap_use_ssl: form.imap_use_ssl,
        username: form.username.trim(),
        password: form.password.trim() || undefined,
        is_enabled: form.is_enabled,
        poll_interval_seconds: Math.max(10, form.poll_interval_seconds),
      });
      setForm((f) => ({ ...f, password: '' }));
      setPasswordSet(true);
      fetchConfig();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Support desk mailbox</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The poller connects to your support mailbox and <strong>imports every message</strong> from configured IMAP
        folders (defaults: INBOX and Junk — many Microsoft 365 messages never reach INBOX). If
        <strong> SON-######</strong> (six digits) in the subject or body matches an <strong>existing</strong> ticket,
        the email is recorded as a customer reply on that ticket; otherwise a <strong>new ticket</strong> is created
        (new SON number) and the message is stored as the opening activity. Processed messages are removed from the
        server. Operators can set <code>SUPPORT_DESK_IMAP_FOLDERS</code> on the poller container for other folder
        names (e.g. regional “Junk Email”). On <strong>System → Feed Status</strong>, “healthy” means the last poll
        succeeded; see container logs <code>sonic_support_desk_mailbox_poller</code> for counts (replies, new tickets,
        skipped already processed).
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Microsoft 365: OAuth (below) is recommended; or IMAP <code>outlook.office365.com</code> with an app password
        only if basic auth is still allowed.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 480 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary">
            Microsoft 365
          </Typography>
          {oauthConnected ? (
            <Alert severity="success">
              Connected with Microsoft 365 (IMAP OAuth). IMAP opens the mailbox:{' '}
              <strong>{form.username || '(set username)'}</strong>
              {' — '}token is for whoever signed in; that account must have Full access to this mailbox in Exchange.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <strong>Shared mailbox (e.g. support@):</strong> Enter that address as <em>Username</em> below first. Then sign in with a{' '}
              <strong>work or school account in your tenant</strong> that has Full access to that mailbox—not a personal @outlook.com
              account. The poller logs into IMAP as the Username while using OAuth from the account you pick.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Outbound ticket mail uses this <strong>Username</strong> for Mailgun <em>From</em> (when the template has no{' '}
            <code>from_email</code>) and for <em>Reply-To</em>, so customers reply to this mailbox—not{' '}
            <code>MAILGUN_REPLY_TO</code> / <code>MAILGUN_FROM_EMAIL</code> (often a cases@ address).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleConnectMicrosoft}
              disabled={oauthBusy}
              startIcon={oauthBusy ? <CircularProgress size={18} /> : <CloudIcon />}
            >
              {oauthBusy ? 'Redirecting…' : 'Sign in with Microsoft'}
            </Button>
            {oauthConnected && (
              <Button variant="text" color="warning" onClick={handleDisconnectMicrosoft} disabled={saving}>
                Disconnect Microsoft
              </Button>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary">
            IMAP server
          </Typography>
          <TextField
            label="IMAP server"
            value={form.imap_server}
            onChange={(e) => setForm({ ...form, imap_server: e.target.value })}
            fullWidth
            placeholder="outlook.office365.com"
          />
          <TextField
            label="Port"
            type="number"
            value={form.imap_port}
            onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })}
            fullWidth
            inputProps={{ min: 1, max: 65535 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.imap_use_ssl}
                onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })}
              />
            }
            label="Use SSL"
          />
          <TextField
            label="Username"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            fullWidth
            placeholder="support@example.com"
          />
          <TextField
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            fullWidth
            disabled={oauthConnected && authMethod === 'oauth_microsoft'}
            placeholder={
              oauthConnected && authMethod === 'oauth_microsoft'
                ? 'Not used while Microsoft OAuth is active'
                : passwordSet
                  ? '•••••••• (leave blank to keep existing)'
                  : 'Enter password'
            }
            helperText={
              oauthConnected && authMethod === 'oauth_microsoft'
                ? 'Save a password to switch back from OAuth (clears Microsoft connection).'
                : passwordSet
                  ? 'Leave blank to keep existing password'
                  : ''
            }
          />
          <TextField
            label="Poll interval (seconds)"
            type="number"
            value={form.poll_interval_seconds}
            onChange={(e) => setForm({ ...form, poll_interval_seconds: parseInt(e.target.value, 10) || 60 })}
            fullWidth
            inputProps={{ min: 10 }}
            helperText="Minimum 10 seconds. How often to check for new emails."
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              />
            }
            label="Enable polling"
          />
          {form.is_enabled && (
            <Typography variant="body2" color="text.secondary">
              {lastPolledAt
                ? `Last polled: ${new Date(lastPolledAt).toLocaleString()}`
                : 'Polling will start on next cycle.'}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <MailIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupportDeskMailboxesTab;
